import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

export default function MiningTable18() {
  const pdfRef = useRef();

  const data = [
    { year: 2018, less_than_high_school: 929, bachelors_or_above: 1325 },
    { year: 2019, less_than_high_school: 877, bachelors_or_above: 1287 },
    { year: 2020, less_than_high_school: 841, bachelors_or_above: 1336 },
    { year: 2021, less_than_high_school: 940, bachelors_or_above: 1398 },
    { year: 2022, less_than_high_school: 1038, bachelors_or_above: 1541 },
    { year: 2023, less_than_high_school: 1078, bachelors_or_above: 1650 },
    { year: 2024, less_than_high_school: 1142, bachelors_or_above: 1733 },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.year),
    datasets: [
      {
        label: "Less than High School",
        data: data.map((d) => d.less_than_high_school),
        borderColor: "#B7950B",
        backgroundColor: "#B7950B",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
      },
      {
        label: "Bachelor’s Degree or Above",
        data: data.map((d) => d.bachelors_or_above),
        borderColor: "#F8BBD0",
        backgroundColor: "#F8BBD0",
        borderWidth: 3,
        tension: 0.4,
        pointRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "🎓 Employment Trend by Education Level (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
      },
    },
  };

  // Summary Insights
  const firstYear = data[0];
  const lastYear = data[data.length - 1];

  const growthLowEdu = (
    ((lastYear.less_than_high_school - firstYear.less_than_high_school) /
      firstYear.less_than_high_school) *
    100
  ).toFixed(1);

  const growthHighEdu = (
    ((lastYear.bachelors_or_above - firstYear.bachelors_or_above) /
      firstYear.bachelors_or_above) *
    100
  ).toFixed(1);

  const latestGap =
    lastYear.bachelors_or_above - lastYear.less_than_high_school;

  const topYear = data.reduce(
    (max, d) =>
      d.bachelors_or_above > max.total
        ? { year: d.year, total: d.bachelors_or_above }
        : max,
    { year: data[0].year, total: data[0].bachelors_or_above },
  );

  // PDF Download
    const downloadPDF = () => {
      const input = pdfRef.current;
      html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: -window.scrollY,
      }).then((canvas) => {
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pageWidth - 10;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;
        let pageNumber = 1;

        const addHeader = () => {
          const logoWidth = 20; // ideal header width
          const logoHeight = 20; // adjust ratio if needed

          pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
          pdf.setFontSize(26);
          pdf.text("UTahData.org", 95, 22);
          pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
          pdf.line(10, 35, 200, 35);
        };

        const addFooter = (pageNumber) => {
          pdf.setFontSize(9);
          const footerY = pageHeight - 10;
          pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
          pdf.text(
            `Downloaded on: ${new Date().toLocaleDateString()}`,
            10,
            footerY,
          );
          pdf.text(
            `Page ${pageNumber}`,
            pageWidth - 10,
            footerY,
            null,
            null,
            "right",
          );
        };

        // First page
        addHeader();
        pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;

        // Add more pages if needed
        while (heightLeft > 0) {
          pdf.addPage();
          pageNumber++;
          addHeader();
          position = heightLeft - imgHeight;
          pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
          heightLeft -= pageHeight - 32;
          addFooter(pageNumber);
        }

        // Final footer
        // ===== FINAL FOOTER SAFE AREA =====

        const footerTopY = pageHeight - 40; // start footer 40mm from bottom
        const footerContentY = pageHeight - 28; // logos row
        const footerBottomTextY = pageHeight - 10; // bottom text row

        // Top Divider Line
        pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

        // UtahData logo
        const utahWidth = 18;
        const utahHeight = 18;
        pdf.addImage(
          utahData,
          "PNG",
          20,
          footerContentY - 8,
          utahWidth,
          utahHeight,
        );

        // Built by
        pdf.setFontSize(10);
        pdf.text("Built by", 45, footerContentY);

        // BODEX logo
        const bodexWidth = 36;
        const bodexHeight = bodexWidth * 0.3;
        pdf.addImage(
          bodex,
          "PNG",
          60,
          footerContentY - 6,
          bodexWidth,
          bodexHeight,
        );

        // Using text
        pdf.text("using", 102, footerContentY);

        // KPI logo
        pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

        // Version
        pdf.text("20250916.v3.198", 140, footerContentY);

        // Bottom Info Line
        pdf.setFontSize(8);
        pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          pageWidth - 10,
          footerBottomTextY,
          null,
          null,
          "right",
        );
        pdf.save("employment_trend_education_gap.pdf");
      });
    };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Trend by Education Level (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Growth (Less than High School)
            </p>
            <p className="text-xl font-bold text-blue-800">+{growthLowEdu}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Growth (Bachelor’s or Above)
            </p>
            <p className="text-xl font-bold text-yellow-800">
              +{growthHighEdu}%
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              2024 Education Gap
            </p>
            <p className="text-xl font-bold text-green-800">
              {latestGap.toLocaleString("en-US")} 👩‍🎓
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="flex flex-col h-full">
            <div className="overflow-x-auto flex-grow">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Less than HS</th>
                    <th className="p-2">Bachelor’s+</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${
                        row.year === 2024
                          ? "bg-green-100 font-bold"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      <td className="p-2">{row.year}</td>
                      <td className="p-2">
                        {row.less_than_high_school.toLocaleString()}
                      </td>
                      <td className="p-2">
                        {row.bachelors_or_above.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
